import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { AlertTriangle, Eye, Trash2 } from "lucide-react";

/**
 * IncidentsTable — Tabla de "Listado de Incidencias" + diálogo de confirmación de borrado.
 *
 * Extraída 1:1 desde `pages/IncidentsPage.js`. Renderiza las incidencias ya filtradas
 * (número, título, cliente, prioridad, estado, asignado, fecha) con acciones de fila
 * (ver detalle y eliminar con guard RBAC `canDelete`). Incluye el `AlertDialog` de borrado.
 *
 * @param {Array}    props.filteredIncidents  Incidencias ya filtradas por búsqueda/estado/prioridad.
 * @param {Object}   props.statusColors       Mapa estado→clases de color del badge.
 * @param {Object}   props.statusLabels       Mapa estado→etiqueta legible.
 * @param {Object}   props.priorityColors     Mapa prioridad→clases de color del badge.
 * @param {Object}   props.priorityLabels     Mapa prioridad→etiqueta legible.
 * @param {Function} props.formatDate         (dateStr) => string — formatea fechas.
 * @param {Function} props.openDetailModal    (incident) => void — abre el modal de detalle.
 * @param {Function} props.canDelete          (resource) => boolean — permiso RBAC para eliminar.
 * @param {Function} props.openDeleteDialog   (event, incident) => void — abre el diálogo de borrado.
 * @param {boolean}  props.deleteDialogOpen   Apertura del diálogo de borrado.
 * @param {Function} props.setDeleteDialogOpen Setter del flag del diálogo de borrado.
 * @param {Object|null} props.incidentToDelete Incidencia pendiente de borrado (para el texto).
 * @param {Function} props.handleDeleteIncident Confirma el borrado permanente.
 */
export const IncidentsTable = ({
  filteredIncidents,
  statusColors,
  statusLabels,
  priorityColors,
  priorityLabels,
  formatDate,
  openDetailModal,
  canDelete,
  openDeleteDialog,
  deleteDialogOpen,
  setDeleteDialogOpen,
  incidentToDelete,
  handleDeleteIncident,
}) => {
  return (
    <>
      {/* Incidents Table */}
      <Card className="bg-[#0A0A35] border-[#1E1E5F]">
        <CardHeader>
          <CardTitle className="text-white font-heading flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            Listado de Incidencias
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[#1E1E5F]">
                  <TableHead className="text-slate-400">Número</TableHead>
                  <TableHead className="text-slate-400">Título</TableHead>
                  <TableHead className="text-slate-400">Cliente</TableHead>
                  <TableHead className="text-slate-400">Prioridad</TableHead>
                  <TableHead className="text-slate-400">Estado</TableHead>
                  <TableHead className="text-slate-400">Asignado a</TableHead>
                  <TableHead className="text-slate-400">Fecha</TableHead>
                  <TableHead className="text-slate-400 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredIncidents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-slate-400 py-8">
                      No hay incidencias que coincidan con los filtros
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredIncidents.map((incident) => (
                    <TableRow key={incident.id} className="border-[#1E1E5F] hover:bg-[#1B1B4B]/50">
                      <TableCell className="text-red-400 font-mono text-sm font-semibold">
                        {incident.incident_number}
                      </TableCell>
                      <TableCell className="text-white max-w-xs truncate">
                        {incident.title}
                      </TableCell>
                      <TableCell className="text-slate-300">
                        {incident.customer_name}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${priorityColors[incident.priority]} text-white`}>
                          {priorityLabels[incident.priority]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusColors[incident.status]} text-white`}>
                          {statusLabels[incident.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-300 text-sm">
                        {incident.assigned_to_name || <span className="text-slate-500 italic">Sin asignar</span>}
                      </TableCell>
                      <TableCell className="text-slate-300 text-sm">
                        {formatDate(incident.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openDetailModal(incident)}
                            className="text-[#03BEE4] hover:text-[#03BEE4]/80"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Ver
                          </Button>
                          {canDelete("incidents") && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => openDeleteDialog(e, incident)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                              data-testid={`delete-incident-${incident.incident_number}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#0A0A35] border-[#1E1E5F]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">¿Eliminar incidencia?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Esta acción eliminará permanentemente la incidencia <span className="text-[#42E695] font-semibold">{incidentToDelete?.incident_number}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#1E1E5F] text-slate-300 hover:bg-[#1E1E5F]">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteIncident}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
